// src/components/CustomerHeader.jsx
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCart, AUTH_CHANGED_EVENT } from "../context/CartContext";
import { APP_MODES } from "../utils/appMode";
import { markJustLoggedOut } from "../utils/biometricLogin";
import { clearRememberedRedirect } from "../utils/authRedirect";
import { unregisterPushTokenOnLogout } from "../notifications/push";
import { handleExternalAnchorClick, isNativeApp } from "../utils/nativeNavigation";
import useSiteNav from "../hooks/useSiteNav";
import "./Header.css";
import "./site-nav.css";

const STIRUP_SITE_URL = "https://www.stirup.it";

function Icon({ name, active = false, className = "" }) {
  const cls = `stir-icon ${active ? "is-active" : ""} ${className}`.trim();

  switch (name) {
    case "search":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <circle
            cx="11"
            cy="11"
            r="6.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M16 16l4.5 4.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "orders":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <rect
            x="4"
            y="5"
            width="16"
            height="15"
            rx="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M8 3.8v3M16 3.8v3M7.5 10.5h9M7.5 14h6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "gift":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path
            d="M4 10h16v10H4z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M3 10h18v-3.5A1.5 1.5 0 0 0 19.5 5H4.5A1.5 1.5 0 0 0 3 6.5V10z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M12 5v15M8.2 5c-1.3 0-2.2-.8-2.2-1.9 0-1 .8-1.8 1.8-1.8C10 1.3 12 5 12 5M15.8 5c1.3 0 2.2-.8 2.2-1.9 0-1-.8-1.8-1.8-1.8C14 1.3 12 5 12 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "user":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <circle
            cx="12"
            cy="8"
            r="3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M5 20c.9-3.4 3.5-5 7-5s6.1 1.6 7 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "cart":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path
            d="M3.5 5h2l1.7 8.5a2 2 0 0 0 2 1.6H17a2 2 0 0 0 1.9-1.4l1.6-5.7H7.1"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="10" cy="19" r="1.5" fill="currentColor" />
          <circle cx="17" cy="19" r="1.5" fill="currentColor" />
        </svg>
      );
    case "logout":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path
            d="M10 17l-5-5 5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5 12h10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M14 5h3a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "wallet":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <rect
            x="3"
            y="6"
            width="18"
            height="12"
            rx="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M16 12h5M17 9.5h4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "store":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path
            d="M4 10h16l-1.2 9H5.2z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M6 10 7.2 5h9.6L18 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "chevron":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path
            d="M7 10l5 5 5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "hamburger":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path
            d="M4 6.5h16M4 12h16M4 17.5h16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    default:
      return null;
  }
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : true,
  );

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);

    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }

    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isDesktop;
}

export default function CustomerHeader({ user, mode, onChangeMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  // Nativo (Capacitor) vs browser: solo per distinguere, sotto i 1024px, il
  // bottom bar nativo (invariato) dal nuovo header mobile-web. Non cambia mai
  // a runtime, quindi non serve ricalcolarlo ad ogni render.
  const nativeApp = useMemo(() => isNativeApp(), []);
  const webMobile = !isDesktop && !nativeApp;

  // Voci-sito (label + url esterni www.stirup.it), allineate dal backend.
  // Usate SOLO nell'header desktop del cliente. Mai vuote: c'è il fallback.
  const { header: siteNavItems } = useSiteNav();

  const token = localStorage.getItem("token");
  const isAuthenticated = !!token;

  const { openCart, items } = useCart();
  const cartCount = (items || []).reduce((sum, it) => {
    const q = Number(it?.qty ?? 0);
    return sum + (Number.isFinite(q) ? q : 0);
  }, 0);

  const role = user?.role || "guest";
  const isFornitore = role === "fornitore";
  const isAdmin = role === "admin";
  const ordersPath = isFornitore ? "/fornitore/ordini" : "/ordini";

  const [accountOpen, setAccountOpen] = useState(false);
  // Solo per il nuovo header mobile-web: pannello hamburger con le voci-sito.
  // Stato separato da accountOpen così i due pannelli restano mutuamente
  // esclusivi senza toccare la semantica di accountOpen usata da desktop e nativo.
  const [siteMenuOpen, setSiteMenuOpen] = useState(false);
  const accountRef = useRef(null);
  const desktopRef = useRef(null);
  const mobileRef = useRef(null);
  const mobileWebHeaderRef = useRef(null);
  // Solo mobile-web: pulsanti hamburger/account come ancore dei rispettivi
  // popup (portal), per calcolarne la posizione via getBoundingClientRect.
  const hamburgerBtnRef = useRef(null);
  const accountTriggerMobileRef = useRef(null);
  const mobileMenuPanelRef = useRef(null);
  const [mobileMenuPos, setMobileMenuPos] = useState(null);

  const closeMenus = () => {
    setAccountOpen(false);
    setSiteMenuOpen(false);
  };

  const handleLogout = () => {
    // Senza await: legge il JWT ora, il logout non aspetta la rete.
    unregisterPushTokenOnLogout();
    localStorage.removeItem("token");
    clearRememberedRedirect(); // niente "pagina richiesta" residua
    // Logout esplicito: sospende SOLO l'auto-trigger biometrico al prossimo
    // arrivo su LoginPage (il bottone FaceID/impronta manuale resta attivo).
    markJustLoggedOut();
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
    closeMenus();
    navigate("/login");
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  useEffect(() => {
    const onDocClick = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false);
        setSiteMenuOpen(false);
      }
    };

    const onKey = (e) => {
      if (e.key === "Escape") closeMenus();
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Solo mobile-web: posizione del popup (account o burger) ancorata al suo
  // pulsante, clampata ai bordi del viewport. Stesso approccio già validato
  // per il popup orari (SupplierHoursButton): calcolo via
  // getBoundingClientRect solo mentre il popup è aperto, mai nel render.
  useLayoutEffect(() => {
    if (!webMobile || (!accountOpen && !siteMenuOpen)) {
      setMobileMenuPos(null);
      return;
    }

    const MARGIN = 8;
    const triggerRef = accountOpen ? accountTriggerMobileRef : hamburgerBtnRef;
    const align = accountOpen ? "right" : "left";

    const updatePosition = () => {
      const btn = triggerRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const panel = mobileMenuPanelRef.current;
      const panelWidth = panel?.offsetWidth || 320;
      const panelHeight = panel?.offsetHeight || 0;

      let left = align === "right" ? rect.right - panelWidth : rect.left;
      left = Math.min(left, window.innerWidth - panelWidth - MARGIN);
      left = Math.max(MARGIN, left);

      let top = rect.bottom + MARGIN;
      if (panelHeight && top + panelHeight > window.innerHeight - MARGIN) {
        top = Math.max(MARGIN, rect.top - panelHeight - MARGIN);
      }

      setMobileMenuPos({ top, left, align });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [webMobile, accountOpen, siteMenuOpen]);

  useEffect(() => {
    const applySpaces = () => {
      const docEl = document.documentElement;
      const desktopH = desktopRef.current
        ? Math.ceil(desktopRef.current.getBoundingClientRect().height)
        : 0;
      const mobileH = mobileRef.current
        ? Math.ceil(mobileRef.current.getBoundingClientRect().height)
        : 0;
      const webMobileH = mobileWebHeaderRef.current
        ? Math.ceil(mobileWebHeaderRef.current.getBoundingClientRect().height)
        : 0;

      // Nativo: bottom bar fissa in basso -> riserva spazio sotto (invariato).
      // Mobile-web: header fisso in alto (come desktop) -> riserva spazio sopra.
      docEl.style.setProperty(
        "--stir-header-top-space",
        isDesktop
          ? `${desktopH + 18}px`
          : webMobile
          ? `${webMobileH + 10}px`
          : "0px",
      );
      docEl.style.setProperty(
        "--stir-header-bottom-space",
        !isDesktop && nativeApp ? `${mobileH + 10}px` : "0px",
      );
    };

    applySpaces();

    const ro = new ResizeObserver(applySpaces);
    if (desktopRef.current) ro.observe(desktopRef.current);
    if (mobileRef.current) ro.observe(mobileRef.current);
    if (mobileWebHeaderRef.current) ro.observe(mobileWebHeaderRef.current);

    window.addEventListener("resize", applySpaces);
    window.addEventListener("orientationchange", applySpaces);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", applySpaces);
      window.removeEventListener("orientationchange", applySpaces);
    };
  }, [isDesktop, webMobile, nativeApp, cartCount, accountOpen, siteMenuOpen, role]);

  useEffect(() => {
    if (isDesktop) {
      document.body.style.overflow = "";
      return;
    }

    document.body.style.overflow = accountOpen || siteMenuOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [accountOpen, siteMenuOpen, isDesktop]);

  // Menu desktop del FORNITORE (invariato). Il cliente, sul desktop, usa invece
  // le voci-sito (nav.is-site-nav più sotto); la bottom bar mobile resta separata
  // e non è toccata (vedi mobileTabs).
  const desktopMainItems = useMemo(() => {
    if (isFornitore) {
      return [
        {
          key: "orders",
          label: "Ordini",
          to: ordersPath,
          icon: "orders",
        },
        {
          key: "services",
          label: "I miei servizi",
          to: "/fornitore/servizi",
          icon: "store",
        },
        {
          key: "subscriptions",
          label: "Abbonamenti",
          to: "/fornitore/abbonamenti",
          icon: "gift",
        },
      ];
    }

    return [];
  }, [isFornitore, ordersPath]);

  const mobileTabs = useMemo(() => {
    if (isFornitore) {
      return [
        {
          key: "orders",
          label: "Ordini",
          to: ordersPath,
          icon: "orders",
        },
        {
          key: "services",
          label: "Servizi",
          to: "/fornitore/servizi",
          icon: "store",
        },
        {
          key: "subscriptions",
          label: "Abb.",
          to: "/fornitore/abbonamenti",
          icon: "gift",
        },
        {
          key: "account",
          label: "Profilo",
          icon: "user",
          action: "account",
        },
      ];
    }

    return [
      {
        key: "search",
        label: "Cerca",
        to: "/dashboard",
        icon: "search",
      },
      {
        key: "orders",
        label: "Ordini",
        to: ordersPath,
        icon: "orders",
      },
      {
        key: "account",
        label: "Profilo",
        icon: "user",
        action: "account",
      },
    ];
  }, [isFornitore, ordersPath]);

  const renderNavItem = (item, mobile = false) => {
    const active =
      item.action === "account"
        ? accountOpen || isActive("/profilo")
        : item.to
        ? isActive(item.to)
        : false;

    const content = (
      <>
        <span className={`stir-nav-icon-wrap ${active ? "is-active" : ""}`}>
          <Icon name={item.icon} active={active} />
        </span>
        <span className={`stir-nav-label ${active ? "is-active" : ""}`}>
          {item.label}
        </span>
      </>
    );

    if (item.action === "account") {
      return (
        <button
          type="button"
          className={`stir-nav-link ${mobile ? "is-mobile" : ""}`}
          onClick={() => setAccountOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={accountOpen}
        >
          {content}
        </button>
      );
    }

    return (
      <Link
        to={item.to}
        className={`stir-nav-link ${mobile ? "is-mobile" : ""}`}
        aria-current={active ? "page" : undefined}
        onClick={closeMenus}
      >
        {content}
      </Link>
    );
  };

  const AccountMenuContent = ({ mobile = false }) => (
    <div
      className={`stir-account-panel stir-account-panel-compact-menu ${
        mobile ? "is-mobile" : ""
      }`}
    >
      <div className="stir-account-head stir-account-head-customer">
        <div className="stir-account-mode-copy">
          <div className="stir-account-title">
            {isAdmin && mode === APP_MODES.CUSTOMER
              ? "Modalità attuale"
              : "Profilo"}
          </div>
          <div className="stir-account-subtitle">
            {isAdmin && mode === APP_MODES.CUSTOMER
              ? "Cliente"
              : isFornitore
              ? "Fornitore"
              : "Cliente"}
          </div>
        </div>

        <div className="stir-account-head-actions">
          {isAdmin &&
            mode === APP_MODES.CUSTOMER &&
            typeof onChangeMode === "function" && (
              <button
                type="button"
                className="stir-mode-box-btn is-admin-return"
                onClick={() => {
                  closeMenus();
                  onChangeMode(APP_MODES.ADMIN);
                }}
              >
                Torna alla vista admin
              </button>
            )}

          {mobile && (
            <button
              type="button"
              className="stir-sheet-close"
              onClick={closeMenus}
              aria-label="Chiudi menu account"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="stir-account-scroll">
        <div className="stir-account-list">
          {!mobile && !isFornitore && (
            <MenuLink
              to="/dashboard"
              label="Cerca"
              icon="search"
              onClick={closeMenus}
            />
          )}

          <MenuLink
            to="/profilo"
            label="Anagrafica"
            icon="user"
            state={mobile ? { resetProfileOverview: true } : undefined}
            onClick={closeMenus}
          />

          {isFornitore && (
            <>
              <MenuLink
                to="/fornitore/servizi"
                label="I miei servizi"
                icon="store"
                onClick={closeMenus}
              />

              <MenuLink
                to="/fornitore/abbonamenti"
                label="Abbonamenti"
                icon="gift"
                onClick={closeMenus}
              />

              <MenuLink
                to="/fornitore/calendario"
                label="Calendario"
                icon="orders"
                onClick={closeMenus}
              />
            </>
          )}

          <MenuLink
            to={ordersPath}
            label="Ordini"
            icon="orders"
            onClick={closeMenus}
          />

          {!isFornitore && (
            <MenuLink
              to="/credito-prepagato"
              label="Credito prepagato"
              icon="wallet"
              onClick={closeMenus}
            />
          )}

          {!isFornitore && (
            <MenuLink
              to="/invita-amico"
              label="Invita un amico"
              icon="gift"
              onClick={closeMenus}
            />
          )}

          {!isFornitore && (
            <MenuLink
              to="/abbonamenti"
              label="Abbonamenti"
              icon="gift"
              onClick={closeMenus}
            />
          )}

          <button
            type="button"
            className="stir-menu-row is-danger"
            onClick={handleLogout}
          >
            <span className="stir-menu-row-left">
              <Icon name="logout" />
              <span>Logout</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isDesktop ? (
        <header className="stir-header-desktop" ref={desktopRef}>
          <div className="stir-header-shell">
            <a
              href={STIRUP_SITE_URL}
              target="_blank"
              rel="noreferrer"
              className="stir-brand stir-brand--logo-only"
              aria-label="Vai al sito StirUp"
              onClick={(e) => handleExternalAnchorClick(e, STIRUP_SITE_URL)}
            >
              <img src="/logo.png" alt="StirUp" className="stir-brand-img" />
            </a>

            {isFornitore ? (
              <nav
                className="stir-desktop-nav"
                aria-label="Navigazione principale"
              >
                {desktopMainItems.map((item) => (
                  <div key={item.key} className="stir-desktop-nav-item">
                    {renderNavItem(item)}
                  </div>
                ))}
              </nav>
            ) : (
              <nav
                className="stir-desktop-nav is-site-nav"
                aria-label="Menu StirUp"
              >
                {(siteNavItems || []).map((it) => (
                  <a
                    key={it.url}
                    className="snav-link"
                    href={it.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => handleExternalAnchorClick(e, it.url)}
                  >
                    {it.label}
                  </a>
                ))}
              </nav>
            )}

            <div className="stir-desktop-actions" ref={accountRef}>
              {!isFornitore && (
                <Link
                  to="/dashboard"
                  className={`stir-icon-btn ${
                    isActive("/dashboard") ? "is-active" : ""
                  }`}
                  aria-label="Cerca"
                  title="Cerca"
                  onClick={closeMenus}
                >
                  <Icon name="search" active={isActive("/dashboard")} />
                </Link>
              )}

              {isAuthenticated && !isFornitore && (
                <button
                  type="button"
                  className="stir-cart-button"
                  onClick={openCart}
                  aria-label="Apri carrello"
                  title="Apri carrello"
                >
                  <Icon name="cart" />
                  {cartCount > 0 && (
                    <span className="stir-cart-badge">{cartCount}</span>
                  )}
                </button>
              )}

              <button
                type="button"
                className={`stir-account-trigger ${
                  accountOpen ? "is-open" : ""
                }`}
                onClick={() => setAccountOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={accountOpen}
              >
                <span className="stir-account-avatar">
                  <Icon name="user" />
                </span>
                <span className="stir-account-trigger-copy">
                  <span className="stir-account-trigger-title">Profilo</span>
                  <span className="stir-account-trigger-subtitle">
                    {isAdmin
                      ? "Admin in vista cliente"
                      : isFornitore
                      ? "Fornitore"
                      : "Cliente"}
                  </span>
                </span>
                <Icon
                  name="chevron"
                  className={`stir-trigger-chevron ${
                    accountOpen ? "is-open" : ""
                  }`}
                />
              </button>

              {accountOpen && (
                <div
                  className="stir-account-dropdown stir-account-dropdown--anim"
                  role="menu"
                >
                  <AccountMenuContent />
                </div>
              )}
            </div>
          </div>
        </header>
      ) : nativeApp ? (
        <>
          <nav
            className={`stir-mobile-nav${!isFornitore ? " stir-mobile-nav--native-customer" : ""}`}
            ref={mobileRef}
            aria-label="Navigazione mobile"
          >
            <div
              className="stir-mobile-nav-inner"
              ref={accountRef}
              style={{
                gridTemplateColumns: `repeat(${
                  mobileTabs.length + (isAuthenticated && !isFornitore ? 1 : 0)
                }, minmax(0, 1fr))`,
              }}
            >
              {mobileTabs.map((item) => (
                <div key={item.key} className="stir-mobile-nav-item">
                  {renderNavItem(item, true)}
                </div>
              ))}

              {isAuthenticated && !isFornitore && (
                <button
                  type="button"
                  className="stir-mobile-cart-fab"
                  onClick={openCart}
                  aria-label="Apri carrello"
                >
                  <Icon name="cart" />
                  {cartCount > 0 && (
                    <span className="stir-cart-badge">{cartCount}</span>
                  )}
                </button>
              )}
            </div>
          </nav>

          {accountOpen && (
            <div
              className="stir-sheet-backdrop"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) closeMenus();
              }}
              onTouchStart={(e) => {
                if (e.target === e.currentTarget) closeMenus();
              }}
            >
              <div
                className="stir-sheet"
                role="menu"
                aria-label="Menu account"
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <div className="stir-sheet-handle" aria-hidden="true" />
                <AccountMenuContent mobile />
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <header className="stir-header-mobile-web" ref={mobileWebHeaderRef}>
            <div className="stir-header-mobile-web-shell" ref={accountRef}>
              <button
                ref={hamburgerBtnRef}
                type="button"
                className="stir-hamburger-btn"
                onClick={() => {
                  setAccountOpen(false);
                  setSiteMenuOpen((v) => !v);
                }}
                aria-haspopup="menu"
                aria-expanded={siteMenuOpen}
                aria-label="Apri menu StirUp"
              >
                <Icon name="hamburger" />
              </button>

              <a
                href={STIRUP_SITE_URL}
                target="_blank"
                rel="noreferrer"
                className="stir-brand stir-brand--logo-only stir-brand--compact"
                aria-label="Vai al sito StirUp"
                onClick={(e) => handleExternalAnchorClick(e, STIRUP_SITE_URL)}
              >
                <img src="/logo.png" alt="StirUp" className="stir-brand-img" />
              </a>

              <div className="stir-header-mobile-web-actions">
                {isFornitore ? (
                  desktopMainItems.map((item) => (
                    <Link
                      key={item.key}
                      to={item.to}
                      className={`stir-icon-btn ${
                        isActive(item.to) ? "is-active" : ""
                      }`}
                      aria-label={item.label}
                      title={item.label}
                      onClick={closeMenus}
                    >
                      <Icon name={item.icon} active={isActive(item.to)} />
                    </Link>
                  ))
                ) : (
                  <>
                    <Link
                      to="/dashboard"
                      className={`stir-icon-btn ${
                        isActive("/dashboard") ? "is-active" : ""
                      }`}
                      aria-label="Cerca"
                      title="Cerca"
                      onClick={closeMenus}
                    >
                      <Icon name="search" active={isActive("/dashboard")} />
                    </Link>

                    <Link
                      to={ordersPath}
                      className={`stir-icon-btn ${
                        isActive(ordersPath) ? "is-active" : ""
                      }`}
                      aria-label="Ordini"
                      title="Ordini"
                      onClick={closeMenus}
                    >
                      <Icon name="orders" active={isActive(ordersPath)} />
                    </Link>

                    {isAuthenticated && (
                      <button
                        type="button"
                        className="stir-icon-btn"
                        onClick={openCart}
                        aria-label="Apri carrello"
                        title="Carrello"
                      >
                        <Icon name="cart" />
                        {cartCount > 0 && (
                          <span className="stir-cart-badge stir-cart-badge--compact">
                            {cartCount}
                          </span>
                        )}
                      </button>
                    )}
                  </>
                )}

                <button
                  ref={accountTriggerMobileRef}
                  type="button"
                  className={`stir-account-trigger stir-account-trigger--compact ${
                    accountOpen ? "is-open" : ""
                  }`}
                  onClick={() => {
                    setSiteMenuOpen(false);
                    setAccountOpen((v) => !v);
                  }}
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                >
                  <span className="stir-account-avatar">
                    <Icon name="user" />
                  </span>
                  <span className="stir-account-trigger-title">Profilo</span>
                  <Icon
                    name="chevron"
                    className={`stir-trigger-chevron ${
                      accountOpen ? "is-open" : ""
                    }`}
                  />
                </button>
              </div>
            </div>
          </header>

          {(accountOpen || siteMenuOpen) &&
            createPortal(
              <div
                className="stir-sheet-backdrop"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) closeMenus();
                }}
                onTouchStart={(e) => {
                  if (e.target === e.currentTarget) closeMenus();
                }}
              >
                <div
                  ref={mobileMenuPanelRef}
                  className="stir-sheet stir-sheet--anchored"
                  role="menu"
                  aria-label={accountOpen ? "Menu account" : "Menu StirUp"}
                  style={
                    mobileMenuPos
                      ? {
                          top: mobileMenuPos.top,
                          left: mobileMenuPos.left,
                          transformOrigin:
                            mobileMenuPos.align === "right"
                              ? "top right"
                              : "top left",
                        }
                      : { visibility: "hidden" }
                  }
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  {accountOpen ? (
                    <AccountMenuContent mobile />
                  ) : (
                    <div className="stir-account-panel stir-account-panel-compact-menu is-mobile">
                      <div className="stir-account-head">
                        <div className="stir-account-title">Menu StirUp</div>
                        <button
                          type="button"
                          className="stir-sheet-close"
                          onClick={() => setSiteMenuOpen(false)}
                          aria-label="Chiudi menu StirUp"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="stir-account-scroll">
                        <div className="stir-account-list">
                          {(siteNavItems || []).map((it) => (
                            <a
                              key={it.url}
                              className="stir-menu-row"
                              href={it.url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => {
                                handleExternalAnchorClick(e, it.url);
                                setSiteMenuOpen(false);
                              }}
                            >
                              <span className="stir-menu-row-left">
                                {it.label}
                              </span>
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>,
              document.body,
            )}
        </>
      )}
    </>
  );
}

function MenuLink({ to, label, icon, onClick, showArrow = false, state }) {
  return (
    <Link to={to} state={state} className="stir-menu-row" onClick={onClick}>
      <span className="stir-menu-row-left">
        <Icon name={icon} />
        <span>{label}</span>
      </span>
      {showArrow ? <span className="stir-menu-row-arrow">›</span> : null}
    </Link>
  );
}
